import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Modal, Accordion, Card, FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { MultiSelect } from 'react-multi-select-component';
import Select from 'react-select';
import { useForm, useFieldArray } from 'react-hook-form';
import './AccountDetails.css';
import { uniq, flatMap } from 'lodash';
import noImage from '../../assets/images/no-image.png';
import { CountriesList, currencyCodes, convertSelectLabel } from '../../Components/helper';
import ImgDropzone from '../../Components/ImgDropzone/ImgDropzone';
import S3Image from '../../Components/Utility/S3Image';
import {
  getProfileDetails,
  getSupplierProfileDetails,
  editProfileDetails,
  editSupplierProfileDetails,
  getUserProfileDetails,
  editUserProfileDetails,
  fetchCurrencyPermission,
} from './store/User.action';
import Loader from '../../Components/Utility/Loader/Loader';
import ChangePasswordModel from './ChangePasswordModel';
import SetSecurityQuestionsModel from './SetSecurityQuestionsModel';
import ResetMFAModal from './ResetMFAModal';
import { getProductAssociatesAction } from '../Library/BomType/Store/BomType.action';
import { getUserList } from '../Supplier/store/Supplier.action';
import { localStorageService } from '../../services/localStorage.service';
import { getAllBrands, getPlacementSubBrand, getSubBrandAccess } from '../../services/SystemMaster.services';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import DistributionCentreSection from './components/DistributionCentreSection';
import { loadUser } from '../Auth/store/Auth.action';
import mainStore from '../../store';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';

const borderStyle = '1px solid #848484';
const changePassword = 'USER_SETTING.ACCOUNT_DETAIL.CHANGE_PASSWORD';
const opacityCss = 'opacity 300ms';
const marginCss = '-1px 0 0 0';
const textCss = 'center !important';
const language = 'USER_SETTING.ACCOUNT_DETAIL.SELECT_LANGUAGE';
const customStyles = {
  option: (provided) => ({
    ...provided,
    padding: '5px 10px',
  }),
  control: () => ({
    display: 'flex',
    alignItems: 'center',
    border: borderStyle,
    height: '40px',
  }),
  singleValue: (provided, state) => {
    const opacity = state.isDisabled ? 0.5 : 1;
    const transition = opacityCss;

    return { ...provided, opacity, transition };
  },
  menu: (provided) => ({
    ...provided,
    margin: marginCss,
    border: borderStyle,
    borderRadius: 0,
    textAlign: textCss,
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    color: '#555',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#848484',
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: 'none',
  }),
};

const countriesList = CountriesList?.map((item) => ({ label: item }));

const createEmptyDistributionCentre = () => ({
  distributionName: '',
  buildingNumber: '',
  streetAddress: '',
  city: '',
  countryName: '',
  postalCode: '',
  currency: '',
  brands: [],
  subBrands: [],
});

const transformDistributionCentreData = (data) => {
  if (!data) return createEmptyDistributionCentre();

  return {
    distributionName: (data?.distributionName || '').toString().trim(),
    buildingNumber: (data?.buildingNumber || '').toString().trim(),
    streetAddress: (data?.streetAddress || '').toString().trim(),
    city: (data?.city || '').toString().trim(),
    countryName: data?.countryName || '',
    postalCode: (data?.postalCode || '').toString().trim(),
    currency: (data?.currency || '').toString().trim(),
    brands: Array.isArray(data?.brands) ? data.brands : [],
    subBrands: Array.isArray(data?.subBrands) ? data.subBrands : [],
  };
};

const normalizeDistributionCentreToArray = (distributionCentreInput) => {
  if (!distributionCentreInput) {
    return [createEmptyDistributionCentre()];
  }

  if (Array.isArray(distributionCentreInput)) {
    const transformedArray = distributionCentreInput.map(transformDistributionCentreData);
    return transformedArray.length > 0 ? transformedArray : [createEmptyDistributionCentre()];
  }
  return [transformDistributionCentreData(distributionCentreInput)];
};

const transformProfileData = (profileData) => {
  if (!profileData) return profileData;

  const transformed = { ...profileData };

  if (transformed.distributionCentre || transformed.distributionCentres) {
    const centreData = transformed.distributionCentres || transformed.distributionCentre;
    transformed.distributionCentre = centreData;
    const normalizedArray = normalizeDistributionCentreToArray(centreData);
    transformed._normalizedDistributionCentres = normalizedArray;
  }

  return transformed;
};

const isDistributionCentreEmpty = (centre = {}) => {
  const distributionName = (centre?.distributionName || '').trim();
  const buildingNumber = (centre?.buildingNumber || '').trim();
  const streetAddress = (centre?.streetAddress || '').trim();
  const city = (centre?.city || '').trim();
  const postalCode = (centre?.postalCode || '').trim();
  const countryName = (typeof centre?.countryName === 'string' ? centre?.countryName : centre?.countryName?.label || '').trim();
  return !distributionName && !buildingNumber && !streetAddress && !city && !postalCode && !countryName;
};

const areAllDistributionCentresEmpty = (centres = []) => {
  if (!Array.isArray(centres) || centres.length === 0) return true;
  return centres.every(isDistributionCentreEmpty);
};

function AccountDetails() {
  const { search, pathname } = useLocation();
  const queryParams = new URLSearchParams(search);
  const querySupplierId = queryParams?.get('supplierId');
  let nonIntegratedSupp = false;
  if (querySupplierId && pathname?.includes('/supplier-account-details')) {
    nonIntegratedSupp = true;
  }

  const [imageSrc, setImageSrc] = useState(noImage);
  const [companyLogo, setCompanyLogo] = useState(noImage);
  /* Uncropped sources kept alongside the displayed (cropped) images. ImgDropzone is seeded from
     these so re-opening the cropper offers the original frame again instead of re-cropping the
     previous crop. They fall back to the saved image, which is all the API exposes. */
  const [imageOriginal, setImageOriginal] = useState(noImage);
  const [companyLogoOriginal, setCompanyLogoOriginal] = useState(noImage);
  const [showImageModel, setShowImageModel] = useState(false);
  const [showCompanyLogoModel, setCompanyLogoModel] = useState(false);
  const [showResetMFAModal, setShowResetMFAModal] = useState(false);
  const [showChangePassModel, setShowChangePassModel] = useState(false);
  const [showChangeQuestions, setShowChangeQuestions] = useState(false);
  const [productComponentCollection, setProductComponentCollection] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [accordianArray, setAaccodionArray] = useState(['distribution-centre']);
  const [brandOptions, setBrandOptions] = useState([]);
  const [subBrandOptions, setSubBrandOptions] = useState([]);
  const [subBrandAccess, setSubBrandAccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    trigger,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      distributionCentres: [createEmptyDistributionCentre()],
    },
  });

  const {
    fields: distributionCentreFields,
    append: appendDistributionCentre,
    remove: removeDistributionCentre,
  } = useFieldArray({
    control,
    name: 'distributionCentres',
  });

  const dispatch = useDispatch();
  const childRef = useRef();
  const originalDistributionCentresRef = useRef([]);
  const {
    Users: { profileDetails, currencyPermission },
    UserDetails: { userInfo },
  } = useSelector((state) => state);

  const currencyOptions = currencyCodes?.map((currKey) => convertSelectLabel(currKey, true));
  const [supplierType, setProductComponentType] = useState([]);
  const [userList, setUserList] = useState([]);
  const [differenceLeadTime, setDifferenceLeadTime] = useState(null);

  const { t, i18n } = useTranslation('common');

  const userId = userInfo?.userId || querySupplierId;
  const role = userInfo?.role;
  const supplierUserId = userInfo?.supplierUserId;

  const shouldRender = useMemo(() => {
    const isAdmin = role === 'Admin';
    const isSupplier = role === 'Supplier' && !supplierUserId;
    const isCompanyUser = role === 'User';
    const isSupplierUser = role === 'Supplier' && supplierUserId;

    return {
      isSupplier,
      isAdmin,
      isCompanyUser,
      isSupplierUser,
      showInTwoCol: isCompanyUser || isSupplierUser || isSupplier,
    };
  }, [role, supplierUserId]);

  useEffect(() => {
    if (!role) return;
    const fetch = async () => {
      const fetchAdminUserDetails = async () => {
        await dispatch(getProfileDetails({ id: userId }));
        await dispatch(fetchCurrencyPermission());
      };

      const supplierDetails = async () => {
        await dispatch(getSupplierProfileDetails({ supplierId: userId }));
      };

      const supplierUserDetails = async () => {
        await dispatch(getUserProfileDetails({ id: supplierUserId }));
      };

      const companyUserDetails = async () => {
        await dispatch(getUserProfileDetails({ id: userId }));
      };

      switch (role) {
        case 'Admin':
          fetchAdminUserDetails();
          break;

        case 'Supplier':
          if (supplierUserId) {
            supplierUserDetails();
          } else {
            supplierDetails();
          }
          break;

        case 'User':
          companyUserDetails();
          break;

        default:
          break;
      }
    };
    fetch();
    setFetching(false);
  }, [dispatch, userId, role, supplierUserId]);

  useEffect(() => {
    setFetching(false);
    const fetch = async () => {
      const supplierDetails = async () => {
        await dispatch(getSupplierProfileDetails({ supplierId: userId }));
      };
      supplierDetails();
    };
    if (querySupplierId !== null && nonIntegratedSupp) {
      fetch();
    }
  }, [querySupplierId, nonIntegratedSupp]);

  /**
   * Loads the Product/Component Type options for this supplier's own tier. The server scopes
   * to Tier 1 by default, so a Tier 2-4 supplier must name its tier or its saved values would
   * not resolve. Re-runs once the profile reports tierType, which arrives after first render.
   */
  const fetchProductAssList = async () => {
    const proAsso = await dispatch(getProductAssociatesAction({ tier: profileDetails?.tierType || 'Tier1' }));
    if (proAsso?.data) {
      const data = proAsso?.data?.map((item) => {
        return {
          label: item?.name,
          value: item?._id,
        };
      });
      const sortedData = data?.sort((a, b) => {
        return a?.label?.localeCompare(b?.label);
      });
      setProductComponentCollection([...(sortedData || [])]);
    }
  };

  useEffect(() => {
    Object?.entries(profileDetails)?.forEach(([key, val]) => {
      if (
        key === 'supplierType' ||
        key === 'country' ||
        key === 'currency' ||
        key === 'internalPointContact' ||
        key === 'companyLogo' ||
        key === 'languageSelected' ||
        key === 'distributionCentre'
      ) {
        if (key === 'supplierType') {
          const sType = profileDetails?.supplierType?.length
            ? productComponentCollection
                ?.filter((item) => profileDetails?.supplierType?.includes(item?.value))
                ?.map((pro) => {
                  return {
                    label: pro?.label,
                    value: pro?.value,
                  };
                })
            : [];
          setProductComponentType(sType);
          setValue('supplierType', sType);
        }

        if (key === 'country') {
          setValue('country', { label: val });
        }
        if (key === 'languageSelected') {
          setValue('language', { label: val });
        }

        if (key === 'internalPointContact' && val !== null && val !== undefined) {
          setValue('internalPointContact', { label: val });
        }

        if (key === 'currency') {
          setValue('currency', { label: val });
        }
        if (key === 'image') {
          setValue('image', `${profileDetails?.image}`);
        }
        if (key === 'companyLogo') {
          if (profileDetails?.companyLogo?.includes('super-admin')) {
            const superAdminLogo = `https://${process.env.REACT_APP_BUCKET_NAME}.s3.${process.env.REACT_APP_AWS_REGION}.amazonaws.com/${profileDetails.companyLogo}`;
            setValue('companyLogo', `${profileDetails?.companyLogo}`);
            setCompanyLogo(superAdminLogo);
            setCompanyLogoOriginal(superAdminLogo);
          } else if (profileDetails?.companyLogo?.includes('s3.eu-west-2')) {
            setValue('companyLogo', profileDetails?.companyLogo);
            setCompanyLogo(profileDetails?.companyLogo);
            setCompanyLogoOriginal(profileDetails?.companyLogo);
          } else {
            const localLogo = `${window?.location?.origin}/${profileDetails?.companyLogo}`;
            setValue('companyLogo', localLogo);
            setCompanyLogo(localLogo);
            setCompanyLogoOriginal(localLogo);
          }
        }
        if (key === 'distributionCentre') {
          const distributionCentreArray = normalizeDistributionCentreToArray(profileDetails?.distributionCentre);

          setValue('distributionCentres', distributionCentreArray);
          originalDistributionCentresRef.current = JSON.parse(JSON.stringify(distributionCentreArray));
        }
      } else {
        setValue(key, val);
      }
    });
    if (profileDetails?.image) {
      setImageSrc(profileDetails?.image);
      setImageOriginal(profileDetails?.image);
    } else {
      setImageSrc(noImage);
      setImageOriginal(noImage);
    }
  }, [profileDetails, setValue, productComponentCollection]);
  const fetchUserList = async () => {
    const uList = await dispatch(getUserList());
    if (uList && uList?.length > 0) {
      const data = uList?.map((item) => {
        return {
          label: `${item?.firstName} ${item?.lastName}`,
          value: item?._id,
        };
      });
      setUserList(data);
    }
  };

  useEffect(() => {
    fetchProductAssList();
    fetchUserList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, role, profileDetails?.tierType]);

  useEffect(() => {
    const rawContact = profileDetails?.internalPointContact;
    if (!rawContact) {
      return;
    }
    const isContactObject = typeof rawContact === 'object';
    const contactId = isContactObject ? rawContact.value || rawContact._id : rawContact;
    const matchedContact = userList?.find((user) => String(user?.value) === String(contactId));
    const labelFallback = isContactObject && rawContact.label ? { label: rawContact.label, value: rawContact.value } : null;
    setValue('internalPointContact', matchedContact || labelFallback);
  }, [profileDetails, userList, setValue]);

  useEffect(() => {
    const newLeadTimeValue = parseInt(watch('newLeadTime')) || 0;
    const previousLeadTimeValue = parseInt(watch('previousLeadTime')) || 0;
    setDifferenceLeadTime(newLeadTimeValue - previousLeadTimeValue);
  }, [watch('newLeadTime'), watch('previousLeadTime')]);

  const onCountryChange = useCallback(
    (country) => {
      setValue('country', country);
    },
    [setValue],
  );
  const onCountryNameChange = useCallback(
    (country, index = 0) => {
      const countryValue = country?.label || country?.value || country || '';
      setValue(`distributionCentres.${index}.countryName`, countryValue);
      trigger(`distributionCentres.${index}.countryName`);
      clearErrors(`distributionCentres.${index}.countryName`);
    },
    [setValue, trigger, clearErrors],
  );

  const handleChangeCurrency = useCallback(
    (currency) => {
      setValue('currency', currency);
    },
    [setValue],
  );

  const onCurrencyChange = useCallback(
    (currency, index = 0) => {
      setValue(`distributionCentres.${index}.currency`, currency?.label || '');
      clearErrors(`distributionCentres.${index}.currency`);
    },
    [setValue, clearErrors],
  );

  useEffect(() => {
    if (role !== 'Admin') return;
    const fetchBrandLists = async () => {
      try {
        const [brandRes, subBrandRes, subBrandToggleRes] = await Promise.all([
          getAllBrands({ forDropdown: true, isActive: 1 }),
          getPlacementSubBrand({ isActive: 1 }),
          getSubBrandAccess(),
        ]);
        const brands = (brandRes?.data || []).map((b) => ({ label: b?.name, value: String(b?._id) }));
        const subBrands = (subBrandRes?.data || []).map((sb) => ({
          label: sb?.name,
          value: String(sb?._id),
          brandId: String(sb?.brandId?._id || sb?.brandId || ''),
        }));
        setBrandOptions(brands);
        setSubBrandOptions(subBrands);
        setSubBrandAccess(subBrandToggleRes?.data || false);
      } catch (err) {
        console.error('Error loading brand/sub-brand options', err);
      }
    };
    fetchBrandLists();
  }, [role]);

  useEffect(() => {
    if (!brandOptions.length && !subBrandOptions.length) return;
    const currentCentres = watch('distributionCentres') || [];
    if (!Array.isArray(currentCentres) || currentCentres.length === 0) return;

    const brandById = new Map(brandOptions.map((b) => [b.value, b]));
    const subBrandById = new Map(subBrandOptions.map((sb) => [sb.value, sb]));

    currentCentres.forEach((centre, index) => {
      const brandsArr = Array.isArray(centre?.brands) ? centre.brands : [];
      const needsBrandHydration = brandsArr.some((b) => typeof b === 'string' || !b?.label);
      if (needsBrandHydration && brandOptions.length) {
        const hydrated = brandsArr
          .map((b) => {
            const id = typeof b === 'string' ? b : String(b?.value || b?._id || '');
            return brandById.get(id) || null;
          })
          .filter(Boolean);
        setValue(`distributionCentres.${index}.brands`, hydrated);
      }

      const subBrandsArr = Array.isArray(centre?.subBrands) ? centre.subBrands : [];
      const needsSubBrandHydration = subBrandsArr.some((s) => typeof s === 'string' || !s?.label);
      if (needsSubBrandHydration && subBrandOptions.length) {
        const hydrated = subBrandsArr
          .map((s) => {
            const id = typeof s === 'string' ? s : String(s?.value || s?._id || '');
            return subBrandById.get(id) || null;
          })
          .filter(Boolean);
        setValue(`distributionCentres.${index}.subBrands`, hydrated);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandOptions, subBrandOptions, profileDetails]);

  const onBrandChange = useCallback(
    (val, index = 0) => {
      const next = Array.isArray(val) ? val : [];
      setValue(`distributionCentres.${index}.brands`, next);
      const allowedBrandIds = new Set(next.map((b) => String(b?.value)));
      const currentSubBrands = watch(`distributionCentres.${index}.subBrands`) || [];
      const filteredSubBrands = currentSubBrands.filter((sb) => allowedBrandIds.has(String(sb?.brandId)));
      if (filteredSubBrands.length !== currentSubBrands.length) {
        setValue(`distributionCentres.${index}.subBrands`, filteredSubBrands);
      }
    },
    [setValue, watch],
  );

  const onSubBrandChange = useCallback(
    (val, index = 0) => {
      setValue(`distributionCentres.${index}.subBrands`, Array.isArray(val) ? val : []);
    },
    [setValue],
  );

  const addDistributionCentre = useCallback(() => {
    const currentCentres = watch('distributionCentres') || [];

    if (currentCentres.length >= 20) {
      toasterMsg(`${t('ACCOUNT_DETAILS.MAXIMUM_LIMIT_REACHED')} 20 ${t('ACCOUNT_DETAILS.DISTRIBUTION_CENTRES_PERIOD')}`, 'danger');
      return;
    }

    appendDistributionCentre(createEmptyDistributionCentre());
  }, [appendDistributionCentre, watch]);

  const handleRemoveDistributionCentre = useCallback(
    (index) => {
      const currentCentres = watch('distributionCentres') || [];

      if (currentCentres.length <= 1) {
        toasterMsg(t('ACCOUNT_DETAILS.DISTRIBUTION_CENTRES_AT_LEAST_ONE'), 'danger');
        return;
      }

      removeDistributionCentre(index);
      toasterMsg(t('ACCOUNT_DETAILS.REMOVE_DISTRIBUTION_CENTRE'), 'success');

      const fieldsToRemove = [
        `distributionCentres.${index}.distributionName`,
        `distributionCentres.${index}.buildingNumber`,
        `distributionCentres.${index}.streetAddress`,
        `distributionCentres.${index}.city`,
        `distributionCentres.${index}.countryName`,
        `distributionCentres.${index}.postalCode`,
      ];
      clearErrors(fieldsToRemove);
    },
    [removeDistributionCentre, watch, clearErrors],
  );

  const getDistributionCentresCount = useCallback(() => {
    return distributionCentreFields.length;
  }, [distributionCentreFields.length]);

  const canAddMoreCentres = useCallback(() => {
    return getDistributionCentresCount() < 20;
  }, [getDistributionCentresCount]);

  const canRemoveCentres = useCallback(() => {
    return getDistributionCentresCount() > 1;
  }, [getDistributionCentresCount]);

  const resetDistributionCentres = useCallback(() => {
    const currentCount = getDistributionCentresCount();
    for (let i = currentCount - 1; i > 0; i--) {
      removeDistributionCentre(i);
    }

    setValue('distributionCentres.0', createEmptyDistributionCentre());

    const fieldsToReset = [
      'distributionCentres.0.distributionName',
      'distributionCentres.0.buildingNumber',
      'distributionCentres.0.streetAddress',
      'distributionCentres.0.city',
      'distributionCentres.0.countryName',
      'distributionCentres.0.postalCode',
    ];
    clearErrors(fieldsToReset);
  }, [getDistributionCentresCount, removeDistributionCentre, setValue, clearErrors]);

  const handleCurrency = (currency) => {
    if (currencyPermission === 'true' || currencyPermission) {
      setValue('currency', currency);
    } else {
      setShowConfirmationPopup(true);
    }
  };

  const handleLanguage = useCallback(
    (language) => {
      setValue('language', language?.value);
    },
    [setValue],
  );
  const onUserChange = useCallback(
    (el) => {
      setValue('internalPointContact', el);
    },
    [setValue],
  );

  const answerValue = (key) => {
    if (key.startsWith('distributionCentres.')) {
      return ['distribution-centre', 'company-information'];
    }
    switch (key) {
      case 'firstName':
      case 'lastName':
      case 'email':
        return 'personal-information';
      case 'companyName':
      case 'buildingNumber':
      case 'city':
      case 'zipCode':
        return 'company-information';
      case 'distributionCentres':
        return ['distribution-centre', 'company-information'];
      case 'distributionCentre':
        return ['distribution-centre', 'company-information'];
      case 'buildingNo':
      case 'townCity':
      case 'country':
        return ['supplier-information', 'additional-information'];
      case 'supplierType':
      case 'code':
      case 'supplierName':
      case 'newLeadTime':
        return 'supplier-information';
      default:
        break;
    }
  };
  useEffect(() => {
    if (Object.keys(errors)?.length > 0) {
      const keynames = Object.keys(errors).map((item) => answerValue(item));
      const newAccordionArray = uniq([...accordianArray, ...flatMap(keynames)]);

      if (JSON.stringify(accordianArray) !== JSON.stringify(newAccordionArray)) {
        setAaccodionArray(newAccordionArray);
      }
    }
  }, [Object?.keys(errors)?.length]);

  const handleAccordionClose = (section) => {
    const sectionFields = {
      'personal-information': ['firstName', 'lastName', 'email'],
      'company-information': ['companyName', 'streetAddress', 'buildingNumber', 'city', 'zipCode'],
      'distribution-centre': [],
      'supplier-information': ['supplierType', 'code', 'supplierName', 'newLeadTime'],
      'additional-information': ['streetAddress', 'buildingNo', 'townCity', 'zipCode', 'country'],
    };

    let fieldsToClear = sectionFields[section] || [];

    if (section === 'distribution-centre') {
      const distributionCentres = watch('distributionCentres') || [];
      fieldsToClear = [];
      distributionCentres.forEach((_, index) => {
        fieldsToClear.push(
          `distributionCentres.${index}.distributionName`,
          `distributionCentres.${index}.buildingNumber`,
          `distributionCentres.${index}.streetAddress`,
          `distributionCentres.${index}.city`,
          `distributionCentres.${index}.countryName`,
          `distributionCentres.${index}.postalCode`,
          `distributionCentres.${index}.currency`,
        );
      });
    }

    clearErrors(fieldsToClear);

    setAaccodionArray((prevState) => prevState?.filter((item) => item !== section));
  };

  const onSubmit = async (values) => {
    const isAdmin = role === 'Admin';

    const extractIds = (arr) =>
      (Array.isArray(arr) ? arr : [])
        .map((item) => {
          if (!item) return '';
          if (typeof item === 'string') return item;
          return String(item?.value || item?._id || '');
        })
        .filter(Boolean);

    let effectiveCentres = [];
    if (isAdmin) {
      const normalizeCentre = (centre) => ({
        distributionName: (centre?.distributionName || '').toString().trim(),
        buildingNumber: (centre?.buildingNumber || '').toString().trim(),
        streetAddress: (centre?.streetAddress || '').toString().trim(),
        city: (centre?.city || '').toString().trim(),
        postalCode: (centre?.postalCode || '').toString().trim(),
        countryName: (typeof centre?.countryName === 'string' ? centre?.countryName : centre?.countryName?.label) ?? '',
        currency: (centre?.currency || '').toString().trim(),
        brands: extractIds(centre?.brands),
        subBrands: extractIds(centre?.subBrands),
      });
      effectiveCentres = (values?.distributionCentres || []).map(normalizeCentre);
    }
    const transformValue = (key, val) => {
      const mappings = {
        country: () => (typeof val?.label === 'object' ? val.label : { label: val?.label }),
        internalPointContact: () => val?.label,
        currency: () => val?.label,
        language: () => val,
        supplierType: () => val?.map((item) => item?.value),
        distributionCentres: () =>
          val?.map((centre, index) => {
            const transformedCentre = {
              distributionName: (centre?.distributionName || '').toString().trim(),
              buildingNumber: (centre?.buildingNumber || '').toString().trim(),
              streetAddress: (centre?.streetAddress || '').toString().trim(),
              city: (centre?.city || '').toString().trim(),
              postalCode: (centre?.postalCode || '').toString().trim(),
              countryName: typeof centre?.countryName === 'object' ? centre.countryName : { label: centre?.countryName },
              currency: (centre?.currency || '').toString().trim(),
              brands: Array.isArray(centre?.brands) ? centre.brands : [],
              subBrands: Array.isArray(centre?.subBrands) ? centre.subBrands : [],
            };

            transformedCentre._index = index;

            return transformedCentre;
          }) || [],
        distributionCentre: () => {
          const firstCentre = val?.[0];
          if (!firstCentre) {
            return {};
          }

          return {
            distributionName: (firstCentre?.distributionName || '').toString().trim(),
            buildingNumber: (firstCentre?.buildingNumber || '').toString().trim(),
            streetAddress: (firstCentre?.streetAddress || '').toString().trim(),
            city: (firstCentre?.city || '').toString().trim(),
            postalCode: (firstCentre?.postalCode || '').toString().trim(),
            countryName: typeof firstCentre?.countryName === 'object' ? firstCentre.countryName : { label: firstCentre?.countryName },
            _isFromMultiCentre: true,
            _totalCentres: val?.length || 0,
          };
        },
      };
      return mappings[key]?.() ?? val;
    };

    if (isAdmin) {
      const distributionCentres = effectiveCentres || [];

      if (distributionCentres.length === 0 || distributionCentreFields.length === 0) {
        toasterMsg(t('ACCOUNT_DETAILS.DISTRIBUTION_CENTRES_AT_LEAST_ONE'), 'danger');
        console.error('At least one distribution centre is required');
        return;
      }

      if (areAllDistributionCentresEmpty(distributionCentres)) {
        toasterMsg('Multi distribution center is required.', 'danger');
        return;
      }

      const requiredFields = ['distributionName', 'buildingNumber', 'streetAddress', 'city', 'countryName', 'postalCode', 'currency'];

      const hasInvalidCentres = distributionCentres.some((centre, index) => {
        const missingFields = requiredFields.filter((field) => !centre[field] || centre[field] === '');
        return missingFields.length > 0;
      });

      if (hasInvalidCentres) {
        toasterMsg(t('USER_SETTING.ACCOUNT_DETAIL.REQUIRED_FIELD'), 'danger');
        return;
      }

      const centreNames = distributionCentres.map((centre) => centre.distributionName?.toLowerCase().trim()).filter(Boolean);
      const uniqueNames = new Set(centreNames);
      if (centreNames.length !== uniqueNames.size) {
        toasterMsg(t('USER_SETTING.ACCOUNT_DETAIL.DUPLICATE_VALUE'), 'danger');
        return;
      }
    }

    const reqObj = Object?.entries(values)?.reduce((acc, [key, val]) => {
      if (key === 'distributionCentres') {
        if (isAdmin) {
          const transformedCentres = transformValue(key, effectiveCentres);
          const transformedSingleCentre = transformValue('distributionCentre', effectiveCentres);
          return {
            ...acc,
            distributionCentres: transformedCentres,
            distributionCentre: transformedSingleCentre,
          };
        }
        return acc;
      }
      return {
        ...acc,
        [key]: transformValue(key, val),
      };
    }, {});

    reqObj._submissionMetadata = {
      distributionCentresCount: isAdmin ? effectiveCentres.length : 0,
      submissionTimestamp: new Date().toISOString(),
      isMultiCentreSubmission: isAdmin ? effectiveCentres.length > 1 : false,
    };

    reqObj.userId = userId;
    reqObj.role = role;
    const editAdminProfile = async () => {
      try {
        const response = await dispatch(editProfileDetails(reqObj, t));

        if (response?.data) {
          if (response?.data?.languageSelected) {
            i18n?.changeLanguage(response?.data?.languageSelected);
          }

          const loggedInUserInfo = localStorageService?.get('loggedInUserInfo');
          if (loggedInUserInfo && response?.data) {
            loggedInUserInfo.companyLogo = response.data.companyLogo;
            const currencyValue = values?.currency?.label || '';
            loggedInUserInfo.currency = currencyValue;
            loggedInUserInfo.companyCurrency = currencyValue;
            localStorageService.remove('loggedInUserInfo');
            localStorageService.set('loggedInUserInfo', loggedInUserInfo);
            setTimeout(async () => {
              await mainStore?.store?.dispatch(loadUser());
            }, 2000);
          }
        }
      } catch (error) {
        console.error('Error submitting admin profile with distribution centres:', error);
      }
    };
    const editSupplierProfile = async () => {
      try {
        if (reqObj?.image === profileDetails?.image) {
          reqObj.image = 'DoNotChange';
        }

        const resp = await dispatch(editSupplierProfileDetails(reqObj, t));

        if (resp?.data) {
          if (resp?.data?.languageSelected) {
            i18n.changeLanguage(resp?.data?.languageSelected);
          }
        }
      } catch (error) {
        console.error('Error submitting supplier profile with distribution centres:', error);
      }
    };
    const editCompanyUserProfile = async () => {
      try {
        const respData = await dispatch(editUserProfileDetails(reqObj, t));

        if (respData?.data) {
          if (respData?.data?.languageSelected) {
            i18n.changeLanguage(respData?.data?.languageSelected);
          }
        }
      } catch (error) {
        console.error('Error submitting user profile with distribution centres:', error);
      }
    };
    const editSupplierUserProfile = async () => {
      try {
        reqObj.userId = supplierUserId;
        const respData = await dispatch(editUserProfileDetails(reqObj, t));

        if (respData?.data) {
          if (respData?.data?.languageSelected) {
            i18n.changeLanguage(respData?.data?.languageSelected);
          }
        }
      } catch (error) {
        console.error('Error submitting supplier user profile with distribution centres:', error);
      }
    };

    switch (role) {
      case 'Admin':
        editAdminProfile();
        break;

      case 'Supplier':
        if (supplierUserId) {
          editSupplierUserProfile();
        } else {
          editSupplierProfile();
        }
        break;

      case 'User':
        editCompanyUserProfile();
        break;

      default:
        break;
    }

    if (nonIntegratedSupp) {
      reqObj.userId = querySupplierId;
      reqObj.role = 'Supplier';
      await dispatch(editSupplierProfileDetails(reqObj, t));
    }
  };

  const toggleChangePasswordModel = () => {
    setShowChangePassModel((prev) => !prev);
  };

  const toggleChangeQuestionsModel = () => {
    setShowChangeQuestions((prev) => !prev);
  };

  const toggleImageModel = () => {
    setShowImageModel((prev) => !prev);
  };

  const toggleCompanyLogoModel = () => {
    setCompanyLogoModel((prev) => !prev);
  };

  const toggleResetMFAModal = () => {
    setShowResetMFAModal((prev) => !prev);
  };

  /**
   * Applies the profile image chosen in the cropper. `originalImg` is the uncropped source the
   * dropzone read from disk; it is retained so a second visit to the cropper starts from the full
   * frame rather than from the crop produced here.
   */
  const handleImageUpload = useCallback(
    async (type) => {
      if (type !== 'close') {
        const { imageRes, originalImg } = await (childRef?.current?.showCroppedImg() || {});
        if (imageRes) {
          setValue('image', imageRes);
          setImageSrc(imageRes);
          setImageOriginal(originalImg || imageRes);
        }
      }
      toggleImageModel();
    },
    [setValue],
  );

  /**
   * Applies the company logo chosen in the cropper. Mirrors handleImageUpload: the uncropped source
   * is retained so re-opening the cropper does not compound the previous crop.
   */
  const uploadCompanyLogo = useCallback(
    async (type) => {
      if (type !== 'close') {
        const { imageRes, originalImg } = await (childRef?.current?.showCroppedImg() || {});
        if (imageRes) {
          setValue('companyLogo', imageRes);
          setCompanyLogo(imageRes);
          setCompanyLogoOriginal(originalImg || imageRes);
        }
      }
      toggleCompanyLogoModel();
    },
    [setValue],
  );

  if (fetching) {
    return <Loader />;
  }

  const validateAccount = (val) => {
    const valueToCheck = typeof val === 'object' ? val?.label : val;
    if (typeof valueToCheck === 'string' && !valueToCheck?.trim()) {
      return t('USER_SETTING.ACCOUNT_DETAIL.REQUIRED_FIELD');
    }
    if (valueToCheck[0] === ' ') {
      return t('USER_SETTING.ACCOUNT_DETAIL.CANNOT_START_WITH_SPACE');
    }

    return null;
  };

  const onNewLeadtimeChange = (e) => {
    if (e !== profileDetails?.newLeadTime) {
      setValue('newLeadTime', e);
      setValue('previousLeadTime', profileDetails?.newLeadTime || 0);
      const diff = e - profileDetails?.newLeadTime;
      setDifferenceLeadTime(diff);
    }
  };

  const displayLeadTime = (displayTime) => {
    if (displayTime > 0) {
      return `+${displayTime}`;
    }
    return `${displayTime}`;
  };

  const languageCode = [{ label: 'English', value: 'en' }];

  const languageOptions = languageCode?.map((langKey) => ({
    label: langKey?.label,
    value: langKey?.value,
  }));

  const checkAccodionActiveState = (section) => accordianArray.includes(section);

  const handleClickToggle = (eventKey) => {
    if (checkAccodionActiveState(eventKey)) {
      handleAccordionClose(eventKey);
    } else {
      setAaccodionArray((prev) => [...prev, eventKey]);
    }
  };
  return (
    <>
      <Container className="account-detail-container">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div className="account-setting-container">
            <div className="collapse-card common-accordion ">
              {shouldRender.isAdmin && (
                <>
                  <Accordion activeKey={checkAccodionActiveState('personal-information') ? 'personal-information' : null} className="mb-3">
                    <Card className="flex-fill ">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.PERSONAL_INFORMATION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="personal-information"
                          onClick={() => handleClickToggle('personal-information')}
                        >
                          <i className={checkAccodionActiveState('personal-information') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="personal-information">
                        <Card.Body>
                          <Row>
                            <Col md={3}>
                              <div className="d-flex">
                                <div className="product-edit-container">
                                  <div className="product-image-container">
                                    <figure className="product-edit-figure mb-0">
                                      <S3Image s3Key={imageSrc} className="img-fluid product-edit-image" alt="profile" />
                                    </figure>
                                  </div>
                                  <div className="upload-button-container upload-account-image">
                                    <button type="button" aria-label="upload-image" className="btn p-0" onClick={toggleImageModel}>
                                      <span className="icon-Upload-1 f-20" />
                                    </button>
                                  </div>
                                </div>
                                <Form.Group className="common-input-control mb-0 pt-0 ml-3 flex-fill">
                                  <p className="f-16 mt-0 mb-0 font-heavy">
                                    {t('USER_SETTING.ACCOUNT_DETAIL.FIRST_NAME')} <span className="required">*</span>
                                  </p>
                                  <Form.Control
                                    {...register('firstName', {
                                      required: t('USER_SETTING.ACCOUNT_DETAIL.FIRST_NAME_REQUIRED'),
                                      validate: validateAccount,
                                    })}
                                    className={watch('firstName') && 'data-filled form-control'}
                                  />
                                  {errors?.firstName?.message && <Form.Text className="error-message">{errors?.firstName?.message}</Form.Text>}
                                </Form.Group>
                              </div>
                            </Col>
                            <Col md={2} sm={2}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.LAST_NAME')} <span className="required">*</span>
                                </p>
                                <Form.Control
                                  {...register('lastName', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.LAST_NAME_REQUIRED'),
                                    validate: validateAccount,
                                  })}
                                  className={watch('lastName') && 'data-filled form-control'}
                                />

                                {errors?.lastName?.message && <Form.Text className="error-message">{errors?.lastName?.message}</Form.Text>}
                              </Form.Group>
                            </Col>

                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.TITLE')}</p>
                                <Form.Control {...register('designation')} className={watch('designation') && 'data-filled form-control'} />
                              </Form.Group>
                            </Col>

                            <Col md={4} sm={4} className="">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.EMAILS')}</p>
                                <Form.Control {...register('email')} className={`${watch('email') && 'data-filled form-control'} disabled-input`} disabled />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  <Accordion activeKey={checkAccodionActiveState('company-information') ? 'company-information' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy ">{t('ACCOUNT_DETAILS.COMPANY_INFORMATION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="company-information"
                          onClick={() => handleClickToggle('company-information')}
                        >
                          <i className={checkAccodionActiveState('company-information') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="company-information">
                        <Card.Body>
                          <Row>
                            {/* <Col md={1} className="pr-md-4 mb-4 mb-md-0"></Col> */}
                            <Col md={3} sm={3}>
                              <div className="d-flex">
                                <div className="product-edit-container ">
                                  <div className="product-image-container">
                                    <figure className="product-edit-figure">
                                      <img
                                        src={companyLogo}
                                        alt="profile"
                                        onError={({ currentTarget }) => {
                                          currentTarget.onerror = null;
                                          currentTarget.src = noImage;
                                        }}
                                        className="img-fluid"
                                      />
                                    </figure>
                                  </div>
                                  <div style={{ marginBottom: 5 }} className="upload-button-container">
                                    <button type="button" aria-label="upload-image" className="btn p-0" onClick={toggleCompanyLogoModel}>
                                      <span className="icon-Upload-1 f-20" />
                                    </button>
                                  </div>
                                </div>
                                <Form.Group className="common-input-control mb-0 pt-0 ml-3 flex-fill">
                                  <p className="f-16 mt-0 mb-0 font-heavy">
                                    {t('USER_SETTING.ACCOUNT_DETAIL.COMPANY_NAME')} <span className="required">*</span>
                                  </p>
                                  <Form.Control
                                    {...register('companyName', {
                                      required: t('USER_SETTING.ACCOUNT_DETAIL.COMPANY_NAME_REQUIRED'),
                                      validate: validateAccount,
                                    })}
                                    className={watch('companyName') && 'data-filled form-control'}
                                  />

                                  {errors?.companyName?.message && <Form.Text className="error-message">{errors?.companyName?.message}</Form.Text>}
                                </Form.Group>
                              </div>
                            </Col>
                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.COMPANY_ADDRESS')} <span className="required">*</span>
                                </p>
                                <Form.Control
                                  {...register('streetAddress', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.STREET_ADDRESS'),
                                    validate: validateAccount,
                                  })}
                                  className={watch('streetAddress') && 'data-filled form-control'}
                                />

                                {errors?.streetAddress?.message && <Form.Text className="error-message">{errors?.streetAddress?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.BUILDING_NO')} <span className="required">*</span>
                                </p>
                                <Form.Control
                                  {...register('buildingNumber', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.BUILDING_NUMBER_REQUIRED'),
                                    validate: validateAccount,
                                  })}
                                  className={watch('buildingNumber') && 'data-filled form-control'}
                                />

                                {errors?.buildingNumber?.message && <Form.Text className="error-message">{errors?.buildingNumber?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.TOWN_CITY')} <span className="required">*</span>
                                </p>
                                <Form.Control
                                  {...register('city', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.CITY'),
                                    validate: validateAccount,
                                  })}
                                  className={watch('city') && 'data-filled form-control'}
                                />

                                {errors?.city?.message && <Form.Text className="error-message">{errors?.city?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.POSTAL_CODE')} <span className="required">*</span>
                                </p>
                                <Form.Control
                                  {...register('zipCode', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.ZIP_CODE'),
                                    validate: validateAccount,
                                  })}
                                  className={watch('zipCode') && 'data-filled form-control'}
                                />

                                {errors?.zipCode?.message && <Form.Text className="error-message">{errors?.zipCode?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <div className="common-react-select flex-fill">
                                  <p className="f-16 mt-0 mb-0 font-heavy">{t('ACCOUNT_DETAILS.COUNTRY')}</p>

                                  <Select
                                    options={countriesList}
                                    getOptionLabel={({ label }) => `${label}`}
                                    getOptionValue={({ label }) => `${label}`}
                                    {...register('country', {
                                      required: t('USER_SETTING.ACCOUNT_DETAIL.COUNTRY_REQUIRED'),
                                      validate: validateAccount,
                                    })}
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    menuPosition="fixed"
                                    onChange={onCountryChange}
                                    placeholder={t('USER_SETTING.ACCOUNT_DETAIL.SELECT_COUNTRY')}
                                    value={watch('country')}
                                  />
                                </div>

                                {errors?.country?.message && <Form.Text className="error-message">{errors?.country?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                            <Col md={3} sm={3}>
                              <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.COMPANY_REGISTRATION_NUMBER')}</p>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <Form.Control {...register('companyRegistration')} className={watch('companyRegistration') && 'data-filled form-control'} />
                              </Form.Group>
                            </Col>

                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.VAT_REGISTRATION_NUMBER')}</p>
                                <Form.Control {...register('vatRegistrationNumber')} className={watch('vatRegistrationNumber') && 'data-filled form-control'} />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>
                              <Accordion
                                activeKey={checkAccodionActiveState('distribution-centre') ? 'distribution-centre' : null}
                                className="mt-0 mb-0 distribution-center-accordian"
                              >
                                <Card className="flex-fill mb-0">
                                  <Card.Header>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center">
                                        <label className="f-16  mb-0 font-heavy">{t('ACCOUNT_DETAILS.MULTI_DISTRIBUTION_CENTRE')}</label>
                                        <Accordion.Toggle
                                          as={Button}
                                          className="btn-toggle d-inline-flex accordion-toggle-icon ml-2"
                                          variant="link"
                                          eventKey="distribution-centre"
                                          onClick={() => handleClickToggle('distribution-centre')}
                                        >
                                          <i
                                            className={
                                              checkAccodionActiveState('distribution-centre') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'
                                            }
                                          />
                                        </Accordion.Toggle>
                                      </div>
                                      <div>
                                        <CustomTooltip
                                          tooltipText={
                                            canAddMoreCentres()
                                              ? t('ACCOUNT_DETAILS.ADD_NEW_DISTRIBUTION_CENTRE')
                                              : t('ACCOUNT_DETAILS.MAXIMUM_CENTRES_ALLOWED', { count: 20 })
                                          }
                                        >
                                          <button
                                            type="button"
                                            className="btn btn-add-bordered"
                                            onClick={addDistributionCentre}
                                            aria-label={
                                              canAddMoreCentres()
                                                ? t('ACCOUNT_DETAILS.ADD_NEW_DISTRIBUTION_CENTRE')
                                                : t('ACCOUNT_DETAILS.MAXIMUM_CENTRES_ALLOWED', { count: 20 })
                                            }
                                            disabled={!canAddMoreCentres()}
                                            data-testid="add-distribution-centre"
                                          >
                                            <i className="icon-Add f-20" />
                                          </button>
                                        </CustomTooltip>
                                      </div>
                                    </div>
                                  </Card.Header>
                                  <Accordion.Collapse eventKey="distribution-centre">
                                    <Card.Body className="min-h-200">
                                      <DistributionCentreSection
                                        fields={distributionCentreFields}
                                        register={register}
                                        errors={errors}
                                        watch={watch}
                                        onCountryNameChange={onCountryNameChange}
                                        onAddCentre={addDistributionCentre}
                                        onRemoveCentre={handleRemoveDistributionCentre}
                                        validateAccount={validateAccount}
                                        canAddMore={canAddMoreCentres()}
                                        canRemove={canRemoveCentres()}
                                        currentCount={getDistributionCentresCount()}
                                        maxCount={20}
                                        currencyOptions={currencyOptions}
                                        onCurrencyChange={onCurrencyChange}
                                        brandOptions={brandOptions}
                                        subBrandOptions={subBrandOptions}
                                        onBrandChange={onBrandChange}
                                        onSubBrandChange={onSubBrandChange}
                                        subBrandAccess={subBrandAccess}
                                      />
                                    </Card.Body>
                                  </Accordion.Collapse>
                                </Card>
                              </Accordion>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  <Accordion activeKey={checkAccodionActiveState('system-url-and-Communication') ? 'system-url-and-Communication' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.SYSTEM_URL_CONNECTION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="system-url-and-Communication"
                          onClick={() => handleClickToggle('system-url-and-Communication')}
                        >
                          <i
                            className={
                              checkAccodionActiveState('system-url-and-Communication') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'
                            }
                          />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="system-url-and-Communication">
                        <Card.Body>
                          <Row className="">
                            <Col md={6} sm={6}>
                              <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.CUSTOM_URL')}</p>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <Form.Control {...register('companyURL')} className={watch('companyURL') && 'data-filled form-control'} disabled />
                                <span className="pos-abs-span">{t('USER_SETTING.ACCOUNT_DETAIL.BOMBYS_PLM')}</span>
                              </Form.Group>
                            </Col>

                            <Col md={6} sm={6}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <div className="common-react-select mb-0 flex-fill">
                                  <p className="f-16 mt-0 mb-0 font-heavy">{t(language)}</p>
                                  <Select
                                    options={languageOptions}
                                    getOptionLabel={({ label }) => `${label}`}
                                    getOptionValue={({ value }) => `${value}`}
                                    {...register('language')}
                                    menuPosition="fixed"
                                    classNamePrefix="common-select"
                                    className="common-select"
                                    onChange={handleLanguage}
                                    value={languageOptions?.find((option) => option?.value === watch('language'))}
                                  />
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  {/* <Accordion activeKey={checkAccodionActiveState("financial-information") ? "financial-information" : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16  mb-0 font-heavy">{t('ACCOUNT_DETAILS.FINANCIAL_INFORMATION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="financial-information"
                          onClick={() => handleClickToggle('financial-information')}
                        >
                          <i className={checkAccodionActiveState('financial-information') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="financial-information">
                        <Card.Body>
                          <Row>
                            <Col md={12} sm={12}>
                              <Form.Group className="mb-0 pt-0">
                                <div className="common-react-select mb-0 flex-fill">
                                  <p className="f-16 font-heavy mb-0">{t('USER_SETTING.ACCOUNT_DETAIL.DEFAULT_CURRENCY')}</p>
                                  <Select
                                    options={currencyOptions}
                                    getOptionLabel={({ label }) => `${label}`}
                                    getOptionValue={({ label }) => `${label}`}
                                    {...register('currency', {
                                      required: t('USER_SETTING.ACCOUNT_DETAIL.CURRENCY_REQUIRED'),
                                      validate: validateAccount,
                                    })}
                                    className="common-select"
                                    menuPosition="fixed"
                                    classNamePrefix="common-select"
                                    onChange={handleCurrency}
                                    placeholder={t('USER_SETTING.ACCOUNT_DETAIL.SELECT_CURRENCY')}
                                    value={watch('currency')}
                                  />
                                </div>
                                {errors?.currency?.message && <Form.Text className="error-message">{errors?.currency?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion> */}
                  <Accordion activeKey={checkAccodionActiveState('account-security') ? 'account-security' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.ACCOUNT_SECURITY')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="account-security"
                          onClick={() => handleClickToggle('account-security')}
                        >
                          <i className={checkAccodionActiveState('account-security') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="account-security">
                        <Card.Body className=" d-flex">
                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 btn account-link-btn mr-16 border-none f-16 text-bold"
                                aria-label="change password"
                                type="button"
                                onClick={toggleChangePasswordModel}
                              >
                                {t(changePassword)}
                              </button>
                            )}
                          </div>
                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 mr-16 account-link-btn btn border-none f-16 text-bold"
                                aria-label="set Security Question"
                                type="button"
                                onClick={toggleChangeQuestionsModel}
                              >
                                {t('USER_SETTING.ACCOUNT_DETAIL.CHANGE_SECURITY_QUSTIONS')}
                              </button>
                            )}
                          </div>
                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 account-link-btn btn border-none f-16 text-bold"
                                aria-label="set Security Question"
                                type="button"
                                onClick={toggleResetMFAModal}
                              >
                                {t('USER_SETTING.ACCOUNT_DETAIL.CHANGE_2FA')}
                              </button>
                            )}
                          </div>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                </>
              )}

              {(shouldRender?.isSupplier || nonIntegratedSupp) && (
                <>
                  <Accordion activeKey={checkAccodionActiveState('personal-information') ? 'personal-information' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.PERSONAL_INFORMATION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="personal-information"
                          onClick={() => handleClickToggle('personal-information')}
                        >
                          <i className={checkAccodionActiveState('personal-information') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="personal-information">
                        <Card.Body>
                          <Row className="justify-content-start align-items-center">
                            <Col md={4} sm={4} className="">
                              <div className="d-flex">
                                <div className="product-edit-container">
                                  <div className="product-image-container">
                                    <figure className="product-edit-figure mb-0">
                                      <S3Image s3Key={imageSrc} className="img-fluid product-edit-image" alt="profile" />
                                    </figure>
                                  </div>
                                  <div className="upload-button-container upload-account-image">
                                    <button type="button" aria-label="upload-image" className="btn p-0" onClick={toggleImageModel}>
                                      <span className="icon-Upload-1 f-20" />
                                    </button>
                                  </div>
                                </div>
                                <Form.Group className="common-input-control mb-0 pt-0 flex-fill ml-3">
                                  <p className="f-16 mt-0 mb-0 font-heavy">
                                    {t('USER_SETTING.ACCOUNT_DETAIL.FIRST_NAME')} <span className="required">*</span>
                                  </p>
                                  <Form.Control
                                    {...register('firstName', {
                                      required: t('USER_SETTING.ACCOUNT_DETAIL.FIRST_NAME_REQUIRED'),
                                      validate: validateAccount,
                                    })}
                                    className={watch('firstName') && 'data-filled form-control'}
                                  />

                                  {errors?.firstName?.message && <Form.Text className="error-message">{errors?.firstName?.message}</Form.Text>}
                                </Form.Group>
                              </div>
                            </Col>
                            <Col md={4} sm={4} className="">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.LAST_NAME')} <span className="required">*</span>
                                </p>
                                <Form.Control
                                  {...register('lastName', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.LAST_NAME_REQUIRED'),
                                    validate: validateAccount,
                                  })}
                                  className={watch('lastName') && 'data-filled form-control'}
                                />

                                {errors?.lastName?.message && <Form.Text className="error-message">{errors?.lastName?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                            <Col md={4} sm={4} className="">
                              <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.EMAILS')}</p>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <Form.Control {...register('email')} className={`${watch('email') && 'data-filled form-control'} disabled-input`} disabled />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  <Accordion activeKey={checkAccodionActiveState('supplier-information') ? 'supplier-information' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.SUPPLIER_INFOTMATION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="supplier-information"
                          onClick={() => handleClickToggle('supplier-information')}
                        >
                          <i className={checkAccodionActiveState('supplier-information') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="supplier-information">
                        <Card.Body>
                          <Row>
                            <Col md={4} sm={4}>
                              <FormGroup className="common-input-control mb-0 pt-0">
                                <div className="common-react-select flex-fill">
                                  <p className="f-16 mt-0 mb-0 font-heavy">
                                    {t('USER_SETTING.ACCOUNT_DETAIL.PRODUCT_COMPONENT_TYPE')}
                                    <span className="required">*</span>{' '}
                                  </p>
                                  <MultiSelect
                                    name="supplierType"
                                    {...register('supplierType', { required: true })}
                                    options={productComponentCollection}
                                    className="product-custom-multiselect common-new-multiselect w-100"
                                    menuPosition="fixed"
                                    value={supplierType}
                                    onChange={(val) => {
                                      setValue('supplierType', val);
                                      setProductComponentType(val);
                                    }}
                                    labelledBy={t('USER_SETTING.ACCOUNT_DETAIL.SELECT_PRODUCT_TYPE')}
                                  />
                                  {errors?.supplierType && (
                                    <span className="error-message">{t('USER_SETTING.ACCOUNT_DETAIL.PRODUCT_COMPONENT_TYPE_REQUIRED')}</span>
                                  )}
                                </div>
                              </FormGroup>
                            </Col>

                            <Col lg="4">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.SUPPLIER_CODE')} <span className="required">*</span>{' '}
                                </p>
                                <Form.Control
                                  {...register('code', {
                                    required: true,
                                  })}
                                  className={watch('code') && 'data-filled form-control'}
                                />

                                {errors?.code && <Form.Text className="error-message">{t('USER_SETTING.ACCOUNT_DETAIL.SUPLIER_CODE_REQUIRED')}</Form.Text>}
                              </Form.Group>
                            </Col>
                            <Col lg="4">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.SUPLIER_NAME')} <span className="required">*</span>{' '}
                                </p>
                                <Form.Control
                                  type="text"
                                  {...register('supplierName', {
                                    required: true,
                                  })}
                                  className={watch('supplierName') && 'data-filled form-control'}
                                />

                                {errors?.supplierName && (
                                  <Form.Text className="error-message"> {t('USER_SETTING.ACCOUNT_DETAIL.SUPLIER_NAME_IS_REQUIRED')}</Form.Text>
                                )}
                              </Form.Group>
                            </Col>
                            <Col lg="4">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.AGENT_FEE')} (%)</p>
                                <Form.Control
                                  type="text"
                                  {...register('agentFee', {
                                    required: false,
                                  })}
                                  className={watch('agentFee') && 'data-filled form-control'}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg="4">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.PARTNER_NOTE')}</p>
                                <Form.Control
                                  type="text"
                                  {...register('note', {
                                    required: false,
                                  })}
                                  className={watch('note') && 'data-filled form-control'}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg="4">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <div className="common-react-select flex-fill">
                                  <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.INTERNAL_POINT_REQUIRED')}</p>
                                  <Select
                                    options={userList}
                                    getOptionLabel={({ label }) => `${label}`}
                                    getOptionValue={({ value }) => `${value}`}
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    menuPosition="fixed"
                                    {...register('internalPointContact', { required: false })}
                                    onChange={onUserChange}
                                    placeholder={t('USER_SETTING.ACCOUNT_DETAIL.SELECT_CONTACT')}
                                    value={watch('internalPointContact')}
                                  />
                                </div>
                              </Form.Group>
                            </Col>

                            <Col lg="4">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.PREVIOUS_LEAD_TIME')}</p>
                                <Form.Control
                                  {...register('previousLeadTime', {
                                    required: false,
                                  })}
                                  disabled
                                  defaultValue={0}
                                  className={watch('previousLeadTime') && 'data-filled form-control'}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg="4">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.LEAD_TIME_DIFFERENCE')}</p>
                                <Form.Control
                                  value={displayLeadTime(differenceLeadTime)}
                                  disabled
                                  className={`"data-filled form-control" ${differenceLeadTime > 0 ? 'text-danger' : 'text-success'}`}
                                />
                              </Form.Group>
                            </Col>
                            <Col lg="4">
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.NEW_LEAD_TIME')} <span className="required">*</span>{' '}
                                </p>
                                <Form.Control
                                  {...register('newLeadTime', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.NEW_LEAD_TIME_REQUIRED'),
                                    pattern: {
                                      value: /^\d+$/,
                                      message: t('USER_SETTING.ACCOUNT_DETAIL.PLEASE_ENTER_VALID_NUMBER'),
                                    },
                                  })}
                                  onChange={(e) => onNewLeadtimeChange(e?.target?.value)}
                                  className={watch('newLeadTime') && 'data-filled form-control'}
                                />

                                {errors?.newLeadTime && errors?.newLeadTime?.message && (
                                  <Form.Text className="error-message">{errors?.newLeadTime?.message}</Form.Text>
                                )}
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row>
                            <Col md={12}>
                              <Accordion activeKey={checkAccodionActiveState('additional-information') ? 'additional-information' : null} className="mb-0 mt-3">
                                <Card className="flex-fill mb-0">
                                  <Card.Header>
                                    <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.ADDITIONAL_INFORMATION')}</label>
                                    <Accordion.Toggle
                                      as={Button}
                                      className="btn-toggle d-inline-flex accordion-toggle-icon"
                                      variant="link"
                                      eventKey="additional-information"
                                      onClick={() => handleClickToggle('additional-information')}
                                    >
                                      <i
                                        className={
                                          checkAccodionActiveState('additional-information') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'
                                        }
                                      />
                                    </Accordion.Toggle>
                                  </Card.Header>
                                  <Accordion.Collapse eventKey="additional-information">
                                    <Card.Body>
                                      <Row>
                                        <Col md={3} sm={3}>
                                          <Form.Group className="common-input-control mb-0 pt-0">
                                            <p className="f-16 mt-0 mb-0 font-heavy">
                                              {t('USER_SETTING.ACCOUNT_DETAIL.STREET_ADDRES')} <span className="required">*</span>{' '}
                                            </p>
                                            <Form.Control
                                              type="text"
                                              {...register('streetAddress', {
                                                required: true,
                                              })}
                                              className={watch('streetAddress') && 'data-filled form-control'}
                                            />

                                            {errors?.streetAddress && (
                                              <Form.Text className="error-message">{t('USER_SETTING.ACCOUNT_DETAIL.STREET_ADDRESS_REQUIRED')}</Form.Text>
                                            )}
                                          </Form.Group>
                                        </Col>
                                        <Col md={2} sm={2}>
                                          <Form.Group className="common-input-control mb-0 pt-0">
                                            <p className="f-16 mt-0 mb-0 font-heavy">
                                              {t('USER_SETTING.ACCOUNT_DETAIL.BUILDING_NO')} <span className="required">*</span>{' '}
                                            </p>
                                            <Form.Control
                                              type="text"
                                              {...register('buildingNo', {
                                                required: true,
                                              })}
                                              className={watch('buildingNo') && 'data-filled form-control'}
                                            />

                                            {errors?.buildingNo && (
                                              <Form.Text className="error-message">{t('USER_SETTING.ACCOUNT_DETAIL.BUILDING_NAME_REQUIRED')}</Form.Text>
                                            )}
                                          </Form.Group>
                                        </Col>
                                        <Col md={2} sm={2}>
                                          <Form.Group className="common-input-control mb-0 pt-0">
                                            <p className="f-16 mt-0 mb-0 font-heavy">
                                              {t('USER_SETTING.ACCOUNT_DETAIL.ENTER_TOWN_CITY')} <span className="required">*</span>{' '}
                                            </p>
                                            <Form.Control
                                              type="text"
                                              {...register('townCity', {
                                                required: true,
                                              })}
                                              className={watch('townCity') && 'data-filled form-control'}
                                            />

                                            {errors?.townCity && (
                                              <Form.Text className="error-message">{t('USER_SETTING.ACCOUNT_DETAIL.TOWN_CITY_REQUIRED')}</Form.Text>
                                            )}
                                          </Form.Group>
                                        </Col>
                                        <Col md={2} sm={2}>
                                          <Form.Group className="common-input-control mb-0 pt-0">
                                            <p className="f-16 mt-0 mb-0 font-heavy">
                                              {t('USER_SETTING.ACCOUNT_DETAIL.ENTER_POSTAL_CODE')} <span className="required">*</span>{' '}
                                            </p>
                                            <Form.Control
                                              type="text"
                                              {...register('zipCode', {
                                                required: true,
                                              })}
                                              className={watch('zipCode') && 'data-filled form-control'}
                                            />

                                            {errors?.zipCode && (
                                              <Form.Text className="error-message">{t('USER_SETTING.ACCOUNT_DETAIL.POSTAL_CODE_REQUIRED')}</Form.Text>
                                            )}
                                          </Form.Group>
                                        </Col>
                                        <Col md={3} sm={3}>
                                          <FormGroup className="mb-0 pt-0">
                                            <div className="common-react-select mb-0 flex-fill">
                                              <p className="f-16 mt-0 mb-0 font-heavy">
                                                {t('USER_SETTING.ACCOUNT_DETAIL.COUNTRY')}
                                                <span className="required">*</span>
                                              </p>
                                              <Select
                                                options={countriesList}
                                                getOptionLabel={({ label }) => `${label}`}
                                                getOptionValue={({ label }) => `${label}`}
                                                {...register('country', {
                                                  required: t('USER_SETTING.ACCOUNT_DETAIL.COUNTRY_REQUIRED'),
                                                  validate: validateAccount,
                                                })}
                                                className="common-select"
                                                classNamePrefix="common-select"
                                                isClearable
                                                menuPosition="fixed"
                                                onChange={onCountryChange}
                                                placeholder={t('USER_SETTING.ACCOUNT_DETAIL.SELECT_COUNTRY')}
                                                value={watch('country')}
                                              />
                                              {errors?.country?.message && <Form.Text className="error-message">{errors?.country?.message}</Form.Text>}
                                            </div>
                                          </FormGroup>
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  </Accordion.Collapse>
                                </Card>
                              </Accordion>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  <Accordion activeKey={checkAccodionActiveState('system-url-and-Communication') ? 'system-url-and-Communication' : null} className="mb-0">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.SYSTEM_URL_CONNECTION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="system-url-and-Communication"
                          onClick={() => handleClickToggle('system-url-and-Communication')}
                        >
                          <i
                            className={
                              checkAccodionActiveState('system-url-and-Communication') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'
                            }
                          />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="system-url-and-Communication">
                        <Card.Body>
                          <Row>
                            <Col md={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <div className="common-react-select mb-0 flex-fill">
                                  <p className="f-16 font-heavy mb-0 mt-0">{t(language)}</p>
                                  <Select
                                    options={languageOptions}
                                    getOptionLabel={({ label }) => `${label}`}
                                    getOptionValue={({ value }) => `${value}`}
                                    {...register('language')}
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    menuPosition="fixed"
                                    styles={customStyles}
                                    onChange={handleLanguage}
                                    value={languageOptions?.find((option) => option?.value === watch('language'))}
                                  />
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  <Accordion activeKey={checkAccodionActiveState('financial-information') ? 'financial-information' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.FINANCIAL_INFORMATION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="financial-information"
                          onClick={() => handleClickToggle('financial-information')}
                        >
                          <i className={checkAccodionActiveState('financial-information') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="financial-information">
                        <Card.Body>
                          <Row>
                            <Col md={3}>
                              <Form.Group className="mb-0 pt-0">
                                <div className="common-react-select mb-0 flex-fill">
                                  <p className="f-16 font-heavy mb-0">
                                    {t('USER_SETTING.ACCOUNT_DETAIL.CURRENCY')}
                                    <span className="required">*</span>{' '}
                                  </p>
                                  <Select
                                    options={currencyOptions}
                                    {...register('currency', { required: t('USER_SETTING.ACCOUNT_DETAIL.CURRENCY_REQUIRED') })}
                                    onChange={handleChangeCurrency}
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    menuPosition="fixed"
                                    placeholder="Select Currency"
                                    value={watch('currency')}
                                  />
                                  {errors?.currency?.message && (
                                    <Form.Text className="error-message">{t('USER_SETTING.ACCOUNT_DETAIL.SUPLIER_CURRENCY_REQUIRED')}</Form.Text>
                                  )}
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  <Accordion activeKey={checkAccodionActiveState('account-security') ? 'account-security' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.ACCOUNT_SECURITY')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="account-security"
                          onClick={() => handleClickToggle('account-security')}
                        >
                          <i className={checkAccodionActiveState('account-security') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="account-security">
                        <Card.Body className=" d-flex">
                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 btn account-link-btn mr-16 border-none f-16 text-bold"
                                aria-label="change password"
                                type="button"
                                onClick={toggleChangePasswordModel}
                              >
                                {t(changePassword)}
                              </button>
                            )}
                          </div>
                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 mr-16 account-link-btn btn border-none f-16 text-bold"
                                aria-label="set Security Question"
                                type="button"
                                onClick={toggleChangeQuestionsModel}
                              >
                                {t('USER_SETTING.ACCOUNT_DETAIL.CHANGE_SECURITY_QUSTIONS')}
                              </button>
                            )}
                          </div>

                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 account-link-btn btn border-none f-16 text-bold"
                                aria-label="set Security Question"
                                type="button"
                                onClick={toggleResetMFAModal}
                              >
                                {t('USER_SETTING.ACCOUNT_DETAIL.CHANGE_2FA')}
                              </button>
                            )}
                          </div>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                </>
              )}
              {(shouldRender?.isSupplierUser || shouldRender?.isCompanyUser) && (
                <>
                  <Accordion activeKey={checkAccodionActiveState('personal-information') ? 'personal-information' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.PERSONAL_INFORMATION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="personal-information"
                          onClick={() => handleClickToggle('personal-information')}
                        >
                          <i className={checkAccodionActiveState('personal-information') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="personal-information">
                        <Card.Body>
                          <Row>
                            <Col md={3} sm={3}>
                              <div className="d-flex">
                                <div className="product-edit-container">
                                  <div className="product-image-container">
                                    <figure className="product-edit-figure mb-0">
                                      <S3Image s3Key={imageSrc} className="img-fluid product-edit-image" alt="profile" />
                                    </figure>
                                  </div>
                                  <div className="upload-button-container upload-account-image">
                                    <button
                                      type="button"
                                      aria-label={t('USER_SETTING.ACCOUNT_DETAIL.UPLOAD_IMAGE')}
                                      className="btn p-0"
                                      onClick={toggleImageModel}
                                    >
                                      <span className="icon-Upload-1 f-20" />
                                    </button>
                                  </div>
                                </div>
                                <Form.Group className="common-input-control mb-0 pt-0 flex-fill ml-3">
                                  <p className="f-16 mt-0 mb-0 font-heavy">
                                    {t('USER_SETTING.ACCOUNT_DETAIL.FIRST_NAME')} <span className="required">*</span>
                                  </p>
                                  <Form.Control
                                    {...register('firstName', {
                                      required: t('USER_SETTING.ACCOUNT_DETAIL.FIRST_NAME_REQUIRED'),
                                      validate: validateAccount,
                                    })}
                                    className={watch('firstName') && 'data-filled form-control'}
                                  />
                                  {errors?.firstName?.message && <Form.Text className="error-message">{errors?.firstName?.message}</Form.Text>}
                                </Form.Group>
                              </div>
                            </Col>
                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.LAST_NAME')} <span className="required">*</span>
                                </p>
                                <Form.Control
                                  {...register('lastName', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.LAST_NAME_REQUIRED'),
                                    validate: validateAccount,
                                  })}
                                  className={watch('lastName') && 'data-filled form-control'}
                                />
                                {errors?.lastName?.message && <Form.Text className="error-message">{errors?.lastName?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">
                                  {t('USER_SETTING.ACCOUNT_DETAIL.TITLE')} <span className="required">*</span>
                                </p>
                                <Form.Control
                                  {...register('title', {
                                    required: t('USER_SETTING.ACCOUNT_DETAIL.TITLE_REQUIRED'),
                                    validate: validateAccount,
                                  })}
                                  className={watch('title') && 'data-filled form-control'}
                                />
                                {errors?.title?.message && <Form.Text className="error-message">{errors?.title?.message}</Form.Text>}
                              </Form.Group>
                            </Col>
                            <Col md={3} sm={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <p className="f-16 mt-0 mb-0 font-heavy">{t('USER_SETTING.ACCOUNT_DETAIL.EMAIL')}</p>
                                <Form.Control {...register('email')} className={watch('email') && 'data-filled form-control'} disabled />
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  <Accordion activeKey={checkAccodionActiveState('system-url-and-Communication') ? 'system-url-and-Communication' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.SYSTEM_URL_CONNECTION')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="system-url-and-Communication"
                          onClick={() => handleClickToggle('system-url-and-Communication')}
                        >
                          <i
                            className={
                              checkAccodionActiveState('system-url-and-Communication') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'
                            }
                          />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="system-url-and-Communication">
                        <Card.Body>
                          <Row>
                            <Col md={3}>
                              <Form.Group className="common-input-control mb-0 pt-0">
                                <div className="common-react-select mb-0 flex-fill">
                                  <p className="f-16 mt-0 mb-0 font-heavy">{t(language)}</p>
                                  <Select
                                    options={languageOptions}
                                    getOptionLabel={({ label }) => `${label}`}
                                    getOptionValue={({ value }) => `${value}`}
                                    {...register('language')}
                                    className="common-select"
                                    classNamePrefix="common-select"
                                    menuPosition="fixed"
                                    styles={customStyles}
                                    onChange={handleLanguage}
                                    value={languageOptions?.find((option) => option?.value === watch('language'))}
                                  />
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                  <Accordion activeKey={checkAccodionActiveState('account-security') ? 'account-security' : null} className="mb-3">
                    <Card className="flex-fill">
                      <Card.Header>
                        <label className="f-16 mb-0 font-heavy">{t('ACCOUNT_DETAILS.ACCOUNT_SECURITY')}</label>
                        <Accordion.Toggle
                          as={Button}
                          className="btn-toggle d-inline-flex accordion-toggle-icon"
                          variant="link"
                          eventKey="account-security"
                          onClick={() => handleClickToggle('account-security')}
                        >
                          <i className={checkAccodionActiveState('account-security') ? 'icon-new-down-arrow-icon inverse' : 'icon-new-down-arrow-icon'} />
                        </Accordion.Toggle>
                      </Card.Header>
                      <Accordion.Collapse eventKey="account-security">
                        <Card.Body className=" d-flex">
                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 btn account-link-btn mr-16 border-none f-16 text-bold"
                                aria-label="change password"
                                type="button"
                                onClick={toggleChangePasswordModel}
                              >
                                {t(changePassword)}
                              </button>
                            )}
                          </div>
                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 mr-16 account-link-btn btn border-none f-16 text-bold"
                                aria-label="set Security Question"
                                type="button"
                                onClick={toggleChangeQuestionsModel}
                              >
                                {t('USER_SETTING.ACCOUNT_DETAIL.CHANGE_SECURITY_QUSTIONS')}
                              </button>
                            )}
                          </div>
                          <div className="accout-link">
                            {!nonIntegratedSupp && (
                              <button
                                className="p-0 account-link-btn btn border-none f-16 text-bold"
                                aria-label="set Security Question"
                                type="button"
                                onClick={toggleResetMFAModal}
                              >
                                {t('USER_SETTING.ACCOUNT_DETAIL.CHANGE_2FA')}
                              </button>
                            )}
                          </div>
                        </Card.Body>
                      </Accordion.Collapse>
                    </Card>
                  </Accordion>
                </>
              )}
            </div>
            <div className="floting-actions">
              <button type="submit" aria-label="btn" className="btn" disabled={isSubmitting}>
                <i className="icon-Save-1 f-24 mr-1" />
              </button>
            </div>
          </div>
        </Form>
      </Container>

      {showImageModel && (
        <div className="modal-body">
          <Modal className="common-custom-modal modal-large" show={showImageModel} backdrop="static" animation>
            <Modal.Header>
              <Modal.Title className="f-24">{t('USER_SETTING.ACCOUNT_DETAIL.UPLOAD_IMAGE')}</Modal.Title>
              <span onClick={() => handleImageUpload('close')} className="icon-close cursor-pointer text-dark text-decoration-none" />
            </Modal.Header>
            <Modal.Body>
              <div className="modal-body">
                <ImgDropzone
                  ref={childRef}
                  title={t('USER_SETTING.ACCOUNT_DETAIL.PROFILE_IMAGE')}
                  acceptedFileExtenstion=".jpeg,.png,.jpg"
                  image={imageOriginal}
                  onSubmit={handleImageUpload}
                />
              </div>
            </Modal.Body>
          </Modal>
        </div>
      )}
      {showCompanyLogoModel && (
        <div className="modal-body">
          <Modal className="common-custom-modal modal-large" show={showCompanyLogoModel} backdrop="static" animation>
            <Modal.Header>
              <Modal.Title className="f-24">{t('USER_SETTING.ACCOUNT_DETAIL.UPLOAD_IMAGES')}</Modal.Title>
              <span onClick={() => uploadCompanyLogo('close')} className="icon-close cursor-pointer text-dark text-decoration-none" />
            </Modal.Header>
            <Modal.Body>
              <div className="modal-body">
                <ImgDropzone
                  ref={childRef}
                  title={t('USER_SETTING.ACCOUNT_DETAIL.COMPANY_LOGO')}
                  acceptedFileExtenstion=".jpeg,.png,.jpg"
                  image={companyLogoOriginal}
                  onSubmit={uploadCompanyLogo}
                />
              </div>
            </Modal.Body>
          </Modal>
        </div>
      )}
      {showChangePassModel && (
        <div className="modal-body">
          <ChangePasswordModel title={t('USER_SETTING.ACCOUNT_DETAIL.CHANGE_PASSWORDS')} show={showChangePassModel} toggle={toggleChangePasswordModel} />
        </div>
      )}
      {showChangeQuestions && (
        <div className="modal-body">
          <SetSecurityQuestionsModel
            show={showChangeQuestions}
            toggle={toggleChangeQuestionsModel}
            title={t('USER_SETTING.ACCOUNT_DETAIL.CHANGE_SECURITY_QUS')}
          />
        </div>
      )}
      {showResetMFAModal && (
        <div className="modal-body">
          <ResetMFAModal show={showResetMFAModal} toggle={toggleResetMFAModal} title={t('USER_SETTING.ACCOUNT_DETAIL.MULTI_FECTOR_AUTHENTICATION')} />
        </div>
      )}
      {showConfirmationPopup && (
        <ConfirmationPopup
          onOpen={showConfirmationPopup}
          onConfirm={() => {
            setShowConfirmationPopup(false);
          }}
          loading={false}
          title={t('ACCOUNT_DETAILS.MODAL_TITLE')}
          message={t('ACCOUNT_DETAILS.MODAL_MESSAGE')}
          toggle={() => {
            setShowConfirmationPopup(false);
          }}
          fromAccountDetails
        />
      )}
    </>
  );
}

export default AccountDetails;
